import _ from 'underscore';
import React, { useCallback, useMemo, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { TextField } from '@bingads-webui-react/fabric-control';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';

import { CustomDropdown } from '../custom-dropdown-component';
import { getImageSizeFilterStyle } from './style';

const ImageSizeFilterInternal = ({
  defaultMinWidth,
  defaultMinHeight,
  dropdownWidth,
  onChange,
  onDismiss,
  focused,
  i18n,
  classes,
}) => {
  // use ref to handle focus switching when pressing tab key
  const minWidthRef = useRef(null);
  const minHeightRef = useRef(null);

  const [minWidth, setMinWidth] = useState(defaultMinWidth);
  const [minHeight, setMinHeight] = useState(defaultMinHeight);

  const getOnChangeCallback = useCallback(setter => (event, newValue) => {
    const val = parseInt(newValue, 10);
    if (_.isEmpty(newValue)) {
      setter(null);
      return;
    }
    if (!_.isNaN(val) && val >= 0 && val <= 1000000) {
      setter(val);
    }
  }, []);

  const onMinWidthChange = useMemo(
    () => getOnChangeCallback(setMinWidth),
    [getOnChangeCallback]
  );

  const onMinHeightChange = useMemo(
    () => getOnChangeCallback(setMinHeight),
    [getOnChangeCallback]
  );

  const onClear = useCallback((isDropdownOpen) => {
    setMinWidth(null);
    setMinHeight(null);
    if (!isDropdownOpen && (defaultMinHeight || defaultMinWidth)) {
      // trigger onchange only when dropdown list is closed
      onChange({ newMinHeight: null, newMinWidth: null });
    }
    if (minWidthRef.current) {
      minWidthRef.current.focus();
    }
  }, [defaultMinHeight, defaultMinWidth, onChange]);

  const title = useMemo(() => {
    if (minWidth && minHeight) {
      return `> ${minWidth}px x ${minHeight}px`;
    }
    if (minWidth) {
      return `${i18n.getString(_TL_('Width'))} > ${minWidth}px`;
    }
    if (minHeight) {
      return `${i18n.getString(_TL_('Height'))} > ${minHeight}px`;
    }
    return '';
  }, [i18n, minHeight, minWidth]);

  const focusTextField = useCallback(textFieldRef => (event) => {
    if (event.key === 'Tab') {
      if (textFieldRef.current) {
        textFieldRef.current.focus();
      }
      if (event.stopPropagation) { event.stopPropagation(); }
      if (event.preventDefault) { event.preventDefault(); }
    }
  }, []);

  const onMinWidthKeyUp = useMemo(() => focusTextField(minHeightRef), [focusTextField]);
  const onMinHeightKeyUp = useMemo(() => focusTextField(minWidthRef), [focusTextField]);

  const onDropdownDismiss = useCallback(() => {
    if (defaultMinHeight !== minHeight || defaultMinWidth !== minWidth) {
      onChange({ newMinWidth: minWidth, newMinHeight: minHeight }, title);
    }
    if (onDismiss) {
      onDismiss({ newMinWidth: minWidth, newMinHeight: minHeight }, title);
    }
  }, [defaultMinHeight, defaultMinWidth, minHeight, minWidth, onChange, onDismiss, title]);

  return (
    <CustomDropdown
      title={title}
      placeholder={i18n.getString(_TL_('Image size'))}
      dropdownWidth={dropdownWidth}
      onClear={onClear}
      onDismiss={onDropdownDismiss}
      focused={focused}
    >
      <div className={classes.imageSizeFilterDowndown}>
        <div className="dropdown-title">{i18n.getString(_TL_('Image size'))}</div>
        <TextField
          ref={minWidthRef}
          className="dropdown-value-input"
          label={i18n.getString(_TL_('Min. width'))}
          suffix="px"
          value={_.isNumber(minWidth) ? minWidth : ''}
          onChange={onMinWidthChange}
          onKeyDown={onMinWidthKeyUp}
        />
        <TextField
          ref={minHeightRef}
          className="dropdown-value-input"
          label={i18n.getString(_TL_('Min. height'))}
          suffix="px"
          value={_.isNumber(minHeight) ? minHeight : ''}
          onChange={onMinHeightChange}
          onKeyDown={onMinHeightKeyUp}
        />
      </div>
    </CustomDropdown>
  );
};

ImageSizeFilterInternal.propTypes = {
  defaultMinWidth: PropTypes.number,
  defaultMinHeight: PropTypes.number,
  onChange: PropTypes.func.isRequired,
  onDismiss: PropTypes.func,
  focused: PropTypes.bool,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  dropdownWidth: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  classes: PropTypes.shape({
    imageSizeFilterDowndown: PropTypes.string,
  }).isRequired,
};

ImageSizeFilterInternal.defaultProps = {
  dropdownWidth: 0,
  defaultMinWidth: null,
  defaultMinHeight: null,
  focused: false,
  onDismiss: null,
};

export const ImageSizeFilter
  = withDefaultStyles(ImageSizeFilterInternal, getImageSizeFilterStyle);
